import type { TransactionCurrency } from '@contracts/payment/Currency';
import type {
  PermitListResponse,
  PermitResponse,
} from '@contracts/permitSearch/PermitListResponse';
import { roundToTwoDecimalsString } from '@contracts/utils/RoundingAmount';
import ArrowForwardSharp from '@mui/icons-material/ArrowForwardSharp';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Typography from '@mui/material/Typography';
import type { GridApi, GridColDef, GridSortModel } from '@mui/x-data-grid-pro';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useClientLabel } from '@src/clients/hooks/useClientLabel';
import { IconButton } from '@src/common/components/IconButton';
import { Stack } from '@src/common/components/Stack';
import { DownloadService } from '@src/common/services/DownloadService';
import { Timestamp } from '@src/common/time/Timestamp';
import { DocumentsClient } from '@src/documents/services/DocumentsClient';
import { PermitStatusBadge } from '@src/orders/components/PermitStatusBadge';
import { transformToPermitStatus } from '@src/orders/domain/PermitApplication';
import { PermitTable } from '@src/permits/permitSearch/PermitTable';
import { PermitTypeFormatter } from '@src/permits/permitSearch/PermitTypeFormatter';
import { useTranslation } from 'react-i18next';

interface Props {
  permits: PermitListResponse;
  isLoading: boolean;
  onSortModelChange: (sortModel: GridSortModel) => void;
  sortModel: GridSortModel;
  onCheckboxChange: (permitId: string, isChecked: boolean) => void;
  onSelectAllChange: (isChecked: boolean) => void;
  selectedPermits: Set<string>;
  onViewOrderClick: (permit: PermitListResponse['permits'][number]) => void;
  gridApiRef?: React.MutableRefObject<GridApi>;
}

export const ResultForReportSearch = ({
  permits,
  isLoading,
  onSortModelChange,
  sortModel,
  onCheckboxChange,
  onSelectAllChange,
  selectedPermits,
  onViewOrderClick,
  gridApiRef,
}: Props) => {
  const { t } = useTranslation();
  const { clientLabel } = useClientLabel();
  const internalApiRef = useGridApiRef();
  const apiRef = gridApiRef || internalApiRef;
  const columns: GridColDef[] = [
    {
      field: 'selection',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: () => (
        <input
          type="checkbox"
          checked={
            selectedPermits.size === permits.permits.length &&
            permits.permits.length > 0
          }
          onChange={(event) => onSelectAllChange(event.target.checked)}
        />
      ),
      renderCell: (params) => (
        <input
          type="checkbox"
          aria-label={`checkbox ${params.row.id}`}
          checked={selectedPermits.has(params.row.id)}
          onChange={(event) =>
            onCheckboxChange(params.row.id, event.target.checked)
          }
        />
      ),
    },
    {
      field: 'submissionDate',
      headerName: t('permitSearch.submissionDate'),
      flex: 2,
    },
    {
      field: 'jurisdiction',
      headerName: t('permitSearch.jurisdiction'),
      flex: 2,
    },
    {
      field: 'pdfDocument',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) =>
        params.row.statePermitDocument && (
          <IconButton
            onClick={() => handleOpenPdf(params.row.statePermitDocument.id)}
            aria-label={`${t('permitSearch.openPdf')} ${params.row.id}`}
          >
            <PictureAsPdfIcon fontSize="small" />
          </IconButton>
        ),
    },
    {
      field: 'type',
      headerName: t('permitSearch.type'),
      flex: 2,
    },
    {
      field: 'name',
      headerName: t('permitSearch.name'),
      flex: 2,
    },
    {
      field: 'agent',
      headerName: t('permitSearch.agent'),
      flex: 2,
    },
    {
      field: 'client',
      headerName: clientLabel,
      flex: 2,
      renderCell: (cell) => renderClientCell(cell.row),
    },
    {
      field: 'stateId',
      headerName: t('permitSearch.stateId'),
      flex: 2,
    },
    {
      field: 'stateFees',
      headerName: t('permitSearch.stateFees'),
      flex: 2,
      renderCell: (params) => (
        <span
          style={{
            textDecoration: params.row.isStateFeesWaived
              ? 'line-through'
              : 'none',
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'plateNumber',
      headerName: t('permitSearch.plateNumber'),
      flex: 2,
    },
    {
      field: 'vin',
      headerName: t('permitSearch.vin'),
      flex: 2,
    },
    {
      field: 'unitNumber',
      headerName: t('permitSearch.unitNumber'),
      flex: 2,
    },
    {
      field: 'invoiceNumber',
      headerName: t('permitSearch.invoiceNumber'),
      flex: 2,
    },
    {
      field: 'billTo',
      headerName: t('fields.billTo'),
      flex: 2,
      renderCell: (cell) => renderBillToCell(cell.row),
    },
    {
      field: 'serviceFees',
      headerName: t('permitSearch.serviceFees'),
      flex: 2,
      renderCell: (params) => (
        <span
          style={{
            textDecoration: params.row.isServiceFeesWaived
              ? 'line-through'
              : 'none',
          }}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: 'status',
      headerName: t('permitSearch.status'),
      renderCell: (cell) => getPermitStatusBadge(cell.value),
      flex: 2,
    },
    {
      field: 'viewOrder',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          onClick={() => onViewOrderClick(params.row.originalPermit)}
          aria-label={`${t('permitSearch.viewOrder')} ${params.row.id}`}
        >
          <ArrowForwardSharp fontSize="small" />
        </IconButton>
      ),
    },
  ];

  const handleOpenPdf = async (documentId: string) => {
    const { blob } = await DocumentsClient.downloadWithId(documentId);
    void DownloadService.openInNewTab(blob);
  };

  const renderClientCell = (row: {
    client: string;
    clientDoingBusinessAs?: string;
  }) => {
    return renderNameCell(row.client, row.clientDoingBusinessAs);
  };

  const renderBillToCell = (row: {
    billTo?: string;
    billToDoingBusinessAs?: string;
  }) => {
    return renderNameCell(row.billTo, row.billToDoingBusinessAs);
  };

  const renderNameCell = (name?: string, doingBusinessAs?: string) => {
    if (!name) return null;
    return (
      <Stack gap={0.5}>
        {doingBusinessAs ? (
          <Stack>
            <Typography variant="body2">{doingBusinessAs}</Typography>
            <Typography variant="caption" color="text.secondary">
              {name}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2">{name}</Typography>
        )}
      </Stack>
    );
  };

  const getFeesRowValue = (fees?: number, currency?: TransactionCurrency) => {
    if (!fees) {
      return null;
    }
    const roundedFees = roundToTwoDecimalsString(fees);
    return '$' + roundedFees + ' ' + (currency || '');
  };

  const formatPermitIds = (permit: PermitResponse) => {
    if (permit.statePermitNumber && permit.stateId) {
      return `${permit.stateId} / ${permit.statePermitNumber} `;
    }
    return permit.statePermitNumber ?? permit.stateId ?? '-';
  };

  const rows = permits.permits.map((permit) => ({
    id: permit.id,
    submissionDate: permit.submissionDate
      ? Timestamp.from(permit.submissionDate).formatUTCDate()
      : undefined,
    type: PermitTypeFormatter.format(permit.type.type),
    name: permit.type.name,
    jurisdiction: permit.type.jurisdiction,
    client: permit.client.name,
    clientDoingBusinessAs: permit.client.doingBusinessAs,
    orderId: permit.orderId,
    stateId: formatPermitIds(permit),
    stateFees: getFeesRowValue(
      permit.fees?.jurisdictionFee?.amount,
      permit.fees?.jurisdictionFee?.currency,
    ),
    isStateFeesWaived: permit.fees?.jurisdictionFee.isWaived,
    serviceFees: getFeesRowValue(
      permit.fees?.serviceFee.amount,
      permit.fees?.serviceFee.currency,
    ),
    isServiceFeesWaived: permit.fees?.serviceFee.isWaived,
    status: permit.status,
    plateNumber: permit.plateNumber,
    unitNumber: permit.unitNumber,
    vin: permit.vin,
    invoiceNumber: permit.invoiceNumber,
    billTo: permit.billTo?.name,
    billToDoingBusinessAs: permit.billTo?.doingBusinessAs,
    statePermitDocument: permit.statePermitDocument,
    originalPermit: permit,
    agent: permit.claimedBy,
  }));

  return (
    <PermitTable
      apiRef={apiRef}
      onSortModelChange={onSortModelChange}
      rows={rows}
      columns={columns}
      sortModel={sortModel}
      isLoading={isLoading}
    />
  );
};

const getPermitStatusBadge = (stringStatus: string) => {
  const status = transformToPermitStatus(stringStatus);
  return <PermitStatusBadge status={status} />;
};
